import React, { useState, useRef, useEffect } from 'react';
import { Send, Trash2, Sparkles, Heart, Brain, MessageCircle, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const AICounselor = ({ user, isEmbedded = false, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Quick start prompts
  const quickPrompts = [
    { icon: Heart, text: "I'm nervous about dating", color: "from-pink-500 to-rose-500" },
    { icon: Brain, text: "Help me manage my anxiety", color: "from-purple-500 to-indigo-500" },
    { icon: MessageCircle, text: "Tips for better conversations", color: "from-emerald-500 to-teal-500" },
    { icon: Sparkles, text: "Boost my confidence", color: "from-amber-500 to-orange-500" },
  ];

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setShowWelcome(false);
    setIsLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post('/counselor/chat', {
        message: messageText.trim(),
        conversation_history: conversationHistory
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          counselor_name: response.data.counselor_name
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to get response. Please try again.');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearHistory = () => {
    setMessages([]);
    setShowWelcome(true);
    toast.success('Conversation cleared');
  };

  const handleQuickPrompt = (prompt) => {
    sendMessage(prompt);
  };

  return (
    <div className={`flex flex-col ${isEmbedded ? 'h-full' : 'min-h-screen'} bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900`}>
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg border-b border-emerald-100 dark:border-slate-700 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors mr-2"
              >
                <ArrowLeft className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </button>
            )}
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800 dark:text-white">Eternity</h1>
              <p className="text-sm text-emerald-600 dark:text-emerald-400">AI Wellness Counsellor</p>
            </div>
          </div>
          
          {messages.length > 0 && (
            <button
              onClick={clearHistory}
              className="flex items-center space-x-2 px-3 py-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-medium">Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Welcome Screen */}
          {showWelcome && messages.length === 0 && (
            <div className="text-center py-8 animate-fade-in">
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Sparkles className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                Hi{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! I'm Eternity 💚
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
                I'm your AI wellness companion. Whether you need dating advice, help managing stress, 
                or just someone to talk to, I'm here for you.
              </p>
              
              {/* Quick Prompts */}
              <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
                {quickPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickPrompt(prompt.text)}
                    className={`p-4 bg-gradient-to-r ${prompt.color} rounded-xl text-white text-left hover:scale-105 transition-transform shadow-lg`}
                    data-testid={`quick-prompt-${index}`}
                  >
                    <prompt.icon className="w-5 h-5 mb-2" />
                    <span className="text-sm font-medium">{prompt.text}</span>
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 mt-8 max-w-sm mx-auto">
                💡 Your conversations are private and not saved. Use the clear button to start fresh anytime.
              </p>
            </div>
          )}

          {/* Chat Messages */}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              <div
                className={`max-w-[85%] md:max-w-[75%] ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl rounded-br-md'
                    : 'bg-white dark:bg-slate-700 text-gray-800 dark:text-white rounded-2xl rounded-bl-md shadow-md border border-emerald-100 dark:border-slate-600'
                } px-4 py-3`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center space-x-2 mb-2 pb-2 border-b border-emerald-100 dark:border-slate-600">
                    <div className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Eternity</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-white dark:bg-slate-700 rounded-2xl rounded-bl-md shadow-md border border-emerald-100 dark:border-slate-600 px-4 py-3">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Eternity is thinking...</span>
                </div>
                <div className="flex space-x-1 mt-2">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg border-t border-emerald-100 dark:border-slate-700 px-4 py-4 sticky bottom-0">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="w-full px-4 py-3 pr-12 bg-emerald-50 dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-gray-800 dark:text-white placeholder-gray-400"
                rows={1}
                disabled={isLoading}
                data-testid="counselor-input"
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className={`p-3 rounded-xl transition-all ${
                inputMessage.trim() && !isLoading
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-lg hover:shadow-xl'
                  : 'bg-gray-200 dark:bg-slate-600 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="send-counselor-msg-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
            Eternity provides support but doesn't replace professional mental health care.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AICounselor;
